import { useWindowSize } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useLayoutEffect, useState, useRef, forwardRef } from 'react';
import { FormattedTime } from 'features/current_user';
const formatCompactTime = value => {
  // Intervals are stored as 24-hour HH:mm (e.g. 17:00), not 12-hour hh:mm.
  const time = moment(value, 'HH:mm');
  return `${time.format('h')}${time.format('a')[0]}`;
};
const containerEditorPreviewCss = "cvje4ql";
const containerCss = "cfv6d4g";
const Container = forwardRef((props, ref) => {
  const {
    isEditorPreview,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerEditorPreviewCss]: isEditorPreview
  }, props.className)} />;
});
const positionWrapperEditorPreviewCss = "p1rqy4v8";
const positionWrapperCss = "psh3s31";
const PositionWrapper = forwardRef((props, ref) => {
  const {
    isEditorPreview,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(positionWrapperCss, {
    [positionWrapperEditorPreviewCss]: isEditorPreview
  }, props.className)} />;
});
const intervalInactiveCss = "i1yq5ydv";
const intervalPaddingCss = "i1ynd34s";
const intervalStrikethroughCss = "i1ltqhqh";
const intervalEditorPreviewCss = "i1p9e9cn";
const intervalCss = "ib8rem1";
const Interval = forwardRef((props, ref) => {
  const {
    inactive,
    padding,
    strikethrough,
    isEditorPreview,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(intervalCss, {
    [intervalInactiveCss]: inactive,
    [intervalPaddingCss]: padding,
    [intervalStrikethroughCss]: strikethrough,
    [intervalEditorPreviewCss]: isEditorPreview
  }, props.className)} />;
});
const moreCss = "msw3esy";
const More = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(moreCss, props.className)} />;
});
const Holiday = ({
  holiday,
  isEditorPreview
}) => {
  if (!holiday) return null;
  return <Interval padding strikethrough={!holiday.enabled} isEditorPreview={isEditorPreview}>
      {holiday.name}
    </Interval>;
};
const DateSpecificIntervals = ({
  holiday,
  inactive,
  intervals,
  maxToShow,
  isEditorPreview
}) => {
  if (!intervals) return null;
  if (holiday?.enabled) return null;
  const shouldShowMore = intervals.length > maxToShow;
  const intervalsToShow = shouldShowMore ? intervals.slice(0, maxToShow - 1) : intervals;
  return <>
      {intervals.length === 0 && <Interval inactive isEditorPreview={isEditorPreview}>
          {I18n.t('publisher.event_types.availability.unavailable')}
        </Interval>}
      {intervalsToShow.map(({
      from,
      to
    }) => <Interval key={`${from}-${to}`} inactive={inactive} isEditorPreview={isEditorPreview}>
          {isEditorPreview ? <>
              {formatCompactTime(from)}
              –
              {formatCompactTime(to)}
            </> : <>
              <FormattedTime value={from} />
              {' – '}
              <FormattedTime value={to} />
            </>}
        </Interval>)}
      {shouldShowMore && <More>{I18n.t('publisher.availability.schedules.more_intervals', {
        count: intervals.length - (maxToShow - 1)
      })}</More>}
    </>;
};
const Intervals = ({
  holiday,
  intervals,
  inactive,
  reduceToFit,
  isEditorPreview = false
}) => {
  const ref = useRef();
  const amountToSubtract = holiday ? 1 : 0;
  const [maxToShow, setMaxToShow] = useState(4);
  const {
    width: windowWidth,
    height: windowHeight
  } = useWindowSize();
  useLayoutEffect(() => {
    if (reduceToFit) {
      const {
        height
      } = ref.current.getBoundingClientRect();
      const maxThatCouldFit = Math.round(height / 26);
      const potentialMax = Math.max(maxThatCouldFit, 2);
      setMaxToShow(Math.min(potentialMax, 10));
    }
  }, [setMaxToShow, reduceToFit, windowWidth, windowHeight]);
  return <Container ref={ref} isEditorPreview={isEditorPreview}>
      <PositionWrapper isEditorPreview={isEditorPreview}>
        <Holiday holiday={holiday} isEditorPreview={isEditorPreview} />
        <DateSpecificIntervals holiday={holiday} inactive={inactive} intervals={intervals} maxToShow={maxToShow - amountToSubtract} isEditorPreview={isEditorPreview} />
      </PositionWrapper>
    </Container>;
};
export default Intervals;

require("./intervals.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./intervals.js");